(() => {
  if (customElements.get('vlr-testimonials')) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  class VlrTestimonials extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.track = this.querySelector('[data-tst-track]');
      this.slides = Array.from(this.querySelectorAll('[data-tst-slide]'));
      if (!this.track || !this.slides.length) return;
      this.prev = this.querySelector('[data-tst-prev]');
      this.next = this.querySelector('[data-tst-next]');
      this.dotsWrap = this.querySelector('[data-tst-dots]');
      this.controls = this.querySelector('[data-tst-controls]');
      this.pauseBtn = this.querySelector('[data-tst-pause]');
      this.section = this.closest('.vlr-tst');
      this.autoplay = this.dataset.autoplay === 'true' && !reduce.matches;
      this.speed = Math.max(3000, (Number(this.dataset.speed) || 5) * 1000);
      this.paused = false;
      this.userPaused = false;
      this.index = 0;

      this.prev && this.prev.addEventListener('click', () => { this.stopForUser(); this.go(this.index - 1); });
      this.next && this.next.addEventListener('click', () => { this.stopForUser(); this.go(this.index + 1); });
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.stopForUser(); this.go(this.index + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.stopForUser(); this.go(this.index - 1); }
      });
      let raf;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.syncFromScroll());
      }, { passive: true });

      if (this.pauseBtn) {
        if (!this.autoplay) this.pauseBtn.hidden = true;
        this.pauseBtn.addEventListener('click', () => {
          this.userPaused = !this.userPaused;
          this.pauseBtn.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
          this.pauseBtn.setAttribute('aria-label', this.userPaused ? this.pauseBtn.dataset.playLabel : this.pauseBtn.dataset.pauseLabel);
          this.userPaused ? this.stop() : this.start();
        });
      }
      ['mouseenter', 'focusin', 'touchstart'].forEach((ev) => this.addEventListener(ev, () => { this.paused = true; }, { passive: true }));
      ['mouseleave', 'focusout'].forEach((ev) => this.addEventListener(ev, () => { this.paused = false; }));

      this._onResize = () => { clearTimeout(this._rt); this._rt = setTimeout(() => this.layout(), 120); };
      window.addEventListener('resize', this._onResize);

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => { this.visible = entries[0].isIntersecting; }, { threshold: 0.3 });
        this.io.observe(this);
      } else { this.visible = true; }

      this._onBlockSelect = (e) => {
        const i = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
        if (i > -1) { this.stop(); this.editorHold = true; this.go(Math.min(i, this.pages - 1), true); }
      };
      this._onBlockDeselect = () => { this.editorHold = false; this.start(); };
      document.addEventListener('shopify:block:select', this._onBlockSelect);
      document.addEventListener('shopify:block:deselect', this._onBlockDeselect);

      this.layout();
      this.section && this.section.classList.add('is-ready');
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      window.removeEventListener('resize', this._onResize);
      document.removeEventListener('shopify:block:select', this._onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this._onBlockDeselect);
      this.io && this.io.disconnect();
    }

    step() {
      const a = this.slides[0].getBoundingClientRect();
      const b = this.slides[1] ? this.slides[1].getBoundingClientRect() : null;
      return b ? b.left - a.left : a.width;
    }

    perView() {
      const w = this.slides[0].getBoundingClientRect().width || 1;
      const cs = getComputedStyle(this.track);
      const inner = this.track.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      return Math.max(1, Math.floor((inner + 2) / w));
    }

    layout() {
      this.pages = Math.max(1, this.slides.length - this.perView() + 1);
      if (this.dotsWrap) {
        this.dotsWrap.textContent = '';
        if (this.pages > 1) {
          const tpl = this.dotsWrap.dataset.label || 'Go to testimonial [n]';
          for (let i = 0; i < this.pages; i++) {
            const d = document.createElement('button');
            d.type = 'button';
            d.className = 'vlr-tst__dot';
            d.setAttribute('aria-label', tpl.replace('[n]', i + 1).replace('[total]', this.pages));
            d.addEventListener('click', () => { this.stopForUser(); this.go(i); });
            this.dotsWrap.appendChild(d);
          }
        }
        this.dotsWrap.hidden = this.pages < 2;
      }
      if (this.controls) this.controls.hidden = this.pages < 2;
      this.syncFromScroll();
    }

    syncFromScroll() {
      const s = this.step() || 1;
      const max = this.track.scrollWidth - this.track.clientWidth;
      let i = Math.round(this.track.scrollLeft / s);
      if (max > 0 && this.track.scrollLeft >= max - 4) i = this.pages - 1;
      this.setActive(Math.min(Math.max(i, 0), this.pages - 1));
    }

    setActive(i) {
      this.index = i;
      if (this.dotsWrap) {
        Array.from(this.dotsWrap.children).forEach((d, n) => d.setAttribute('aria-current', n === i ? 'true' : 'false'));
      }
      if (!this.autoplay) {
        if (this.prev) this.prev.disabled = i <= 0;
        if (this.next) this.next.disabled = i >= this.pages - 1;
      }
    }

    go(i, instant) {
      if (this.autoplay) {
        if (i < 0) i = this.pages - 1;
        if (i > this.pages - 1) i = 0;
      } else {
        i = Math.min(Math.max(i, 0), this.pages - 1);
      }
      const left = this.slides[i].offsetLeft - this.slides[0].offsetLeft;
      this.track.scrollTo({ left, behavior: instant || reduce.matches ? 'auto' : 'smooth' });
      this.setActive(i);
    }

    start() {
      if (!this.autoplay || this.userPaused || this.editorHold || this.timer) return;
      this.timer = setInterval(() => {
        if (this.paused || !this.visible || document.hidden || this.pages < 2) return;
        this.go(this.index + 1);
      }, this.speed);
    }

    stop() { clearInterval(this.timer); this.timer = null; }

    stopForUser() {
      if (!this.autoplay || this.userPaused) return;
      this.userPaused = true;
      this.stop();
      if (this.pauseBtn) {
        this.pauseBtn.setAttribute('aria-pressed', 'true');
        this.pauseBtn.setAttribute('aria-label', this.pauseBtn.dataset.playLabel);
      }
    }
  }

  customElements.define('vlr-testimonials', VlrTestimonials);
})();
